import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Certificate } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { Download, Award, Calendar, User, FileText } from 'lucide-react'

export function CertificatePage() {
  useDocumentTitle('認定証')
  const { certificateId } = useParams<{ certificateId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [certificate, setCertificate] = useState<Certificate | null>(null)

  useEffect(() => {
    if (!user || !certificateId) return
    fetchCertificate()
  }, [certificateId, user])

  const fetchCertificate = async () => {
    if (!certificateId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 認定証情報を取得
      const { data: certData, error: certError } = await supabase
        .from('certificates')
        .select('*')
        .eq('certificate_id', certificateId)
        .eq('user_id', user.id)
        .single()

      if (certError || !certData) {
        setError('認定証が見つかりませんでした')
        setLoading(false)
        return
      }

      setCertificate(certData)
    } catch (err: any) {
      console.error('Error fetching certificate:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (certificate?.pdf_url) {
      window.open(certificate.pdf_url, '_blank')
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="認定証を読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !certificate) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '認定証が見つかりませんでした'} />
          <div className="mt-4">
            <button
              onClick={() => navigate('/mypage')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/mypage')}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            ← マイページへ戻る
          </button>
        </div>

        <div className="text-center mb-8">
          <Award className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">認定証</h1>
          <p className="text-gray-600">Certificate of Completion</p>
        </div>

        {/* 認定証プレビュー */}
        <div className="bg-white rounded-lg shadow-lg p-12 mb-8 border-4 border-yellow-500">
          <div className="text-center space-y-6">
            <div>
              <Award className="mx-auto h-20 w-20 text-yellow-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">認定証</h2>
              <p className="text-sm text-gray-600">Certificate of Completion</p>
            </div>

            <div className="py-8 border-t border-b border-gray-200">
              <p className="text-lg text-gray-700 mb-4">これを証する</p>
              <p className="text-3xl font-bold text-gray-900 mb-6">
                {certificate.candidate_name}
              </p>
              <p className="text-gray-700 mb-2">上記の者は</p>
              <p className="text-xl font-semibold text-gray-900 mb-6">
                {certificate.course_title}
              </p>
              <p className="text-gray-700">を修了したことを証明します</p>
            </div>

            <div className="grid grid-cols-2 gap-8 text-sm text-gray-700">
              <div>
                <p className="text-gray-600 mb-1">認定日</p>
                <p className="font-medium">
                  {certificate.certified_at
                    ? new Date(certificate.certified_at).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">認定番号</p>
                <p className="font-mono font-medium">{certificate.certificate_no}</p>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">認証団体</p>
              <p className="font-medium text-gray-900">{certificate.tenant_name}</p>
            </div>
          </div>
        </div>

        {/* 認定証情報 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">認定証情報</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">氏名</p>
                <p className="font-medium">{certificate.candidate_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">講座名</p>
                <p className="font-medium">{certificate.course_title}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Award className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">認定番号</p>
                <p className="font-mono font-medium">{certificate.certificate_no}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">認定日</p>
                <p className="font-medium">
                  {certificate.certified_at
                    ? new Date(certificate.certified_at).toLocaleDateString('ja-JP')
                    : '-'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-5 w-5" /> {/* spacer */}
              <div className="flex-1">
                <p className="text-sm text-gray-600">得点</p>
                <p className="font-medium">{certificate.score}点</p>
              </div>
            </div>
          </div>
        </div>

        {/* ダウンロードボタン */}
        <div className="text-center">
          {certificate.pdf_url ? (
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
            >
              <Download className="h-5 w-5" />
              PDFをダウンロード
            </button>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                PDF版の認定証は現在生成中です。しばらくお待ちください。
              </p>
            </div>
          )}
        </div>

        {/* 注意事項 */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">ご注意</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>この認定証は、指定された講座を修了したことを証明します</li>
            <li>認定番号は一意であり、認定証の真正性を確認するために使用されます</li>
            <li>認定証の内容を改変・偽造することは禁止されています</li>
          </ul>
        </div>
      </div>
    </MainLayout>
  )
}
